import { useEffect, useMemo, useState } from 'react'

type UseResendCountdownParams = {
  createdAt?: string | null
  expiresInSeconds?: number | null
  waitSeconds?: number | null
}

const formatCountdown = (secondsTotal: number) => {
  const minutes = Math.floor(secondsTotal / 60)
  const seconds = secondsTotal % 60

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const toTimestamp = (value?: string | null) => {
  if (!value) {
    return null
  }

  const timestamp = new Date(value).getTime()

  return Number.isFinite(timestamp) ? timestamp : null
}

export const useResendCountdown = ({
  createdAt,
  expiresInSeconds,
  waitSeconds
}: UseResendCountdownParams) => {
  const [nowMs, setNowMs] = useState(() => Date.now())
  const createdAtMs = useMemo(() => toTimestamp(createdAt), [createdAt])

  useEffect(() => {
    if (createdAtMs === null) {
      return
    }

    const timer = window.setInterval(() => setNowMs(Date.now()), 1000)

    return () => window.clearInterval(timer)
  }, [createdAtMs])

  return useMemo(() => {
    const waitTotal = Math.max(0, Math.ceil(Number(waitSeconds ?? 0)))

    if (createdAtMs === null) {
      return {
        expired: expiresInSeconds != null,
        formattedTime: '',
        canResend: waitTotal === 0,
        resendInSeconds: waitTotal
      }
    }

    const currentNowMs = Math.max(nowMs, createdAtMs)
    const secondsPassed = Math.max(
      0,
      Math.floor((currentNowMs - createdAtMs) / 1000)
    )
    const resendInSeconds = Math.max(0, waitTotal - secondsPassed)
    const canResend = resendInSeconds === 0
    const hasExpiry =
      typeof expiresInSeconds === 'number' && Number.isFinite(expiresInSeconds)

    if (!hasExpiry) {
      return {
        expired: false,
        formattedTime: '',
        canResend,
        resendInSeconds
      }
    }

    const secondsRemaining = Math.max(
      0,
      Math.ceil(expiresInSeconds) - secondsPassed
    )

    return {
      expired: secondsRemaining === 0,
      formattedTime:
        secondsRemaining === 0 ? '' : formatCountdown(secondsRemaining),
      canResend,
      resendInSeconds
    }
  }, [createdAtMs, expiresInSeconds, nowMs, waitSeconds])
}
